import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import {
  supportsFileSystemAccess,
  getParentFolder,
  getBuffingFolder,
  buffingAuthFilename,
  elementToPdfBlob,
  writeBlobToFolder,
  getStoredFolderName,
} from '../utils/pdfSave';
import { getISOWeek } from '../weekUtils';

const YELLOW = '#FFD000';
const BLACK  = '#0a0a0a';
const NAVY   = '#1e3a4a';

const DEPT_EMAILS = {
  'KIA':       'gmcaullay@melvillekia.com.au',
  'SUZUKI':    'rbonini@melvillemotors.com.au',
  'GWM':       'rbonini@melvillemotors.com.au',
  'GAC':       'rbonini@melvillemotors.com.au',
  'USED CARS': 'anew@melvillemotors.com.au',
  // Service Manager: James Stewart from October 2026 (replaced Aaron Merrett).
  // Mirrors DEPT_EMAILS in backend/services/emailService.js — change both.
  'SERVICE':   'jstewart@melvillekia.com.au',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function statusBadge(s) {
  const cfg = {
    pending:  { bg: '#fef3c7', color: '#92400e', label: '⏳ Pending' },
    approved: { bg: '#d1fae5', color: '#065f46', label: '✓ Approved' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: '✕ Rejected' },
  }[s] || { bg: '#f3f4f6', color: '#374151', label: s };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 0, fontSize: 11, fontWeight: 700 }}>
      {cfg.label}
    </span>
  );
}

/* ── Email Preview Modal ─────────────────────────────────────────────────── */
function EmailPreviewModal({ request, onConfirm, onSavePdf, onCancel, sending, savingPdf, mode = 'approve' }) {
  const isResend = mode === 'resend';
  const defaultTo = DEPT_EMAILS[request.department] || '';
  const defaultCC = 'ndavies@melvillemotors.com.au, michelcarrascoza@actscardetailing.com.au';

  const [editing,     setEditing]     = React.useState(false);
  // Email fields
  const [toEmail,     setToEmail]     = React.useState(defaultTo);
  const [ccEmail,     setCcEmail]     = React.useState(defaultCC);
  const [notes,       setNotes]       = React.useState('');
  // Data fields
  const [date,        setDate]        = React.useState(request.date || '');
  const [department,  setDepartment]  = React.useState(request.department || '');
  const [stockNumber, setStockNumber] = React.useState(request.stock_number || '');
  const [carModel,    setCarModel]    = React.useState(request.car_model || '');
  const [authorizedBy,setAuthorizedBy]= React.useState(request.authorized_by || '');
  const [scope,       setScope]       = React.useState(request.scope || '');

  // Ref to the rendered email body — same DOM block we serialize into a PDF
  // when the admin chooses "Save as PDF" instead of (or alongside) emailing.
  const emailBodyRef = React.useRef(null);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 0, border: '1px solid #d1d5db',
    fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
    background: '#fff', color: BLACK,
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#9ca3af',
    textTransform: 'uppercase', marginBottom: 5, display: 'block',
  };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '16px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 0, width: '100%', maxWidth: 660,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #e0e0e0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: BLACK }}>
              {isResend ? 'Resend Email' : 'Email Preview'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
              {editing ? 'Editing — changes reflect in the preview below'
                : isResend ? 'Review and resend the authorization email'
                : 'Review before sending'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setEditing(e => !e)}
              disabled={sending}
              style={{
                padding: '6px 14px', borderRadius: 0, border: `1px solid ${editing ? NAVY : '#e0e0e0'}`,
                background: editing ? NAVY : '#f9fafb',
                color: editing ? '#fff' : '#374151',
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
              }}
            >
              {editing ? '✓ Done Editing' : '✎ Edit'}
            </button>
            <button onClick={onCancel} style={{
              background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
              color: '#9ca3af', lineHeight: 1, padding: '0 4px',
            }}>✕</button>
          </div>
        </div>

        {/* Edit panel */}
        {editing && (
          <div style={{ overflow: 'auto', padding: '16px 24px', borderBottom: '2px solid #fbbf24', background: '#fffbeb', display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>

            {/* Section: Email */}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', letterSpacing: 1, textTransform: 'uppercase' }}>📧 Email</div>
            <div>
              <label style={labelStyle}>To (recipient)</label>
              <input value={toEmail} onChange={e => setToEmail(e.target.value)} style={inputStyle} placeholder="email@example.com" />
            </div>
            <div>
              <label style={labelStyle}>CC (comma-separated)</label>
              <input value={ccEmail} onChange={e => setCcEmail(e.target.value)} style={inputStyle} />
            </div>

            {/* Section: Form data */}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>📋 Form Data</div>
            <div style={row2}>
              <div>
                <label style={labelStyle}>Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <select value={department} onChange={e => {
                  setDepartment(e.target.value);
                  const auto = DEPT_EMAILS[e.target.value];
                  if (auto) setToEmail(auto);
                }} style={inputStyle}>
                  {['KIA','SUZUKI','GWM','GAC','USED CARS','SERVICE'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                  {!['KIA','SUZUKI','GWM','GAC','USED CARS','SERVICE'].includes(department) && (
                    <option value={department}>{department}</option>
                  )}
                </select>
              </div>
            </div>
            <div style={row2}>
              <div>
                <label style={labelStyle}>Stock Number</label>
                <input value={stockNumber} onChange={e => setStockNumber(e.target.value.toUpperCase())} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Car Model</label>
                <input value={carModel} onChange={e => setCarModel(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Authorized By</label>
              <input value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)} style={inputStyle} placeholder="e.g. NOAH DAVIES" />
            </div>
            <div>
              <label style={labelStyle}>Scope (area to buff)</label>
              <input value={scope} onChange={e => setScope(e.target.value)} style={inputStyle} placeholder="e.g. ROOF, BONNET, FULL CAR" />
            </div>
            <div>
              <label style={labelStyle}>Extra notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Any additional information to include in the email..."
              />
            </div>
          </div>
        )}

        {/* Email meta bar (always visible when not editing) */}
        {!editing && (
          <div style={{ padding: '14px 24px', borderBottom: '1px solid #f0f0f0', background: '#f9fafb', flexShrink: 0 }}>
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
              <span style={{ color: '#9ca3af', fontWeight: 600, display: 'inline-block', width: 30 }}>To:</span>
              <strong>{toEmail || '(no email configured)'}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
              <span style={{ color: '#9ca3af', fontWeight: 600, display: 'inline-block', width: 30 }}>CC:</span>
              {ccEmail}
            </div>
            <div style={{ fontSize: 12, color: '#374151' }}>
              <span style={{ color: '#9ca3af', fontWeight: 600, display: 'inline-block', width: 30 }}>Re:</span>
              Buffing Authorization — {department} | {stockNumber} | {carModel}
            </div>
          </div>
        )}

        {/* Email body preview — always visible, updates live */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          <div ref={emailBodyRef} style={{
            border: '1px solid #e0e0e0', borderRadius: 0, overflow: 'hidden',
            fontFamily: 'Arial, sans-serif', fontSize: 14,
          }}>
            <img
              src="/buffing-header.jpg"
              alt="Acts Car Detailing"
              style={{ display: 'block', width: '100%', height: 160, objectFit: 'cover', objectPosition: 'center 30%' }}
            />
            <div style={{ background: '#fff', textAlign: 'center', padding: '20px 30px 14px', borderBottom: '3px solid #000' }}>
              <div style={{ fontFamily: '"Arial Black", Arial, sans-serif', fontSize: 20, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: '#000' }}>
                BUFFING AUTHORIZATION FORM
              </div>
            </div>

            <div style={{ padding: '0 30px' }}>
              {[
                ['DATE',         date],
                ['DEPARTMENT',   department],
                ['STOCK NUMBER', stockNumber],
                ['CAR MODEL',    carModel],
              ].map(([label, val], i, arr) => (
                <div key={label} style={{ padding: '14px 0', borderBottom: i < arr.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                  <div style={{ fontSize: 10, fontWeight: 'bold', letterSpacing: 2, color: '#999', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#cc0000' }}>{val || '—'}</div>
                </div>
              ))}

              <div style={{ padding: '16px 0 12px' }}>
                <div style={{
                  background: '#f4f4f4', borderLeft: '4px solid #000', borderRadius: 0,
                  padding: '14px 18px', fontSize: 13, lineHeight: 1.8, color: '#000',
                }}>
                  I <strong>{authorizedBy}</strong> authorize Acts Car Detailing to perform the buffing service on the car described above.<br />
                  The service will be performed on: <strong>{scope}</strong>
                  {notes && <><br /><br /><em style={{ color: '#555' }}>{notes}</em></>}
                </div>
              </div>
            </div>

            <div style={{ background: '#0a0a0a', padding: '14px 30px', textAlign: 'center', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
              <span style={{ color: '#FFD000' }}>Acts Car Detailing</span>
              <span style={{ color: '#555' }}> · Perth, Western Australia</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <button onClick={onCancel} disabled={sending || savingPdf} style={{
            flex: '1 1 100px', padding: '12px', borderRadius: 0, border: '1px solid #e0e0e0',
            background: '#f9fafb', color: '#374151', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>
            Cancel
          </button>

          {/* Save PDF — primary action now that the email pipeline is unreliable.
              IMPORTANT: this button must be the FIRST async step the user kicks
              off so the directory picker can consume the live click gesture. */}
          <button
            onClick={() => onSavePdf({
              fields: { toEmail, ccEmail, scope, notes, date, department, stockNumber, carModel, authorizedBy },
              emailBodyEl: emailBodyRef.current,
            })}
            disabled={sending || savingPdf}
            style={{
              flex: '2 1 200px', padding: '12px', borderRadius: 0, border: `2px solid ${NAVY}`,
              background: savingPdf ? '#d1d5db' : '#fff',
              color: savingPdf ? '#9ca3af' : NAVY,
              fontWeight: 900, fontSize: 14, cursor: savingPdf ? 'not-allowed' : 'pointer',
            }}
          >
            {savingPdf ? 'Saving PDF…' : '💾 Save as PDF'}
          </button>

          <button
            onClick={() => onConfirm({ toEmail, ccEmail, scope, notes, date, department, stockNumber, carModel, authorizedBy })}
            disabled={sending || savingPdf}
            style={{
              flex: '2 1 200px', padding: '12px', borderRadius: 0, border: 'none',
              background: sending ? '#d1d5db' : BLACK,
              color: sending ? '#9ca3af' : YELLOW,
              fontWeight: 900, fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending...' : isResend ? '✉ Resend Email' : '✉ Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────────────── */
export default function BuffingAdmin() {
  const [requests,    setRequests]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [filter,      setFilter]      = useState('pending');
  const [acting,      setActing]      = useState(null);   // id being rejected
  const [preview,     setPreview]     = useState(null);   // request being previewed
  const [previewMode, setPreviewMode] = useState('approve'); // 'approve' | 'resend'
  const [sending,     setSending]     = useState(false);
  const [savingPdf,   setSavingPdf]   = useState(false);
  const [folderName,  setFolderName]  = useState(null);   // remembered OneDrive root

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.getBuffingAll(filter);
      setRequests(rows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Surface the remembered OneDrive folder so admin sees where saves will go.
  useEffect(() => {
    getStoredFolderName().then(setFolderName).catch(() => setFolderName(null));
  }, []);

  /* Open preview modal instead of window.confirm */
  function handleApproveClick(request) {
    setPreviewMode('approve');
    setPreview(request);
  }

  function handleResendClick(request) {
    setPreviewMode('resend');
    setPreview(request);
  }

  /* Save the buffing authorization as a PDF into the week folder that
   * matches the authorization date:
   *   OneDrive parent / {year} YEAR / WEEK {n} / BUFFING AUTORIZATION /
   *   DATE_MODEL_STOCK.pdf
   * (same WEEK {n} the paperwork for that week uses).
   * If approving a pending request, also flip its DB status to approved
   * (this calls the same approveBuffing endpoint — email may fail in the
   * background but the PDF save is what we actually rely on).
   *
   * IMPORTANT: getParentFolder() may need to show the directory picker on
   * first use. That requires a live user gesture, so it must be the FIRST
   * await in the click chain — no fetches before it.
   */
  async function handleSavePdf({ fields, emailBodyEl }) {
    if (!preview) return;
    if (!emailBodyEl) {
      alert('Could not find the authorization preview to save. Please reopen the modal.');
      return;
    }
    if (!supportsFileSystemAccess()) {
      alert('Saving PDFs to a folder requires Chrome or Edge on desktop. Please open this page in one of those browsers.');
      return;
    }

    setSavingPdf(true);
    try {
      // Step 1: resolve folder (consumes the user gesture — no awaits before this).
      const parent = await getParentFolder();
      if (!parent) throw new Error('No folder selected. Please pick the OneDrive reports folder.');
      // Derive the ISO week from the authorization date so the PDF lands in
      // that week's folder. An undated request falls back to the old flat
      // BUFFING AUTORIZATION folder (getBuffingFolder handles the fallback).
      const isoDate = fields.date || '';
      let year, week;
      if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
        year = parseInt(isoDate.slice(0, 4), 10);
        week = getISOWeek(isoDate).week;
      }
      const buffingFolder = await getBuffingFolder(parent, year, week);

      // Step 2: render the preview to a PDF blob.
      const blob = await elementToPdfBlob(emailBodyEl, {
        title:       `Buffing Authorization — ${fields.stockNumber || ''}`,
        format:      'A4',
        orientation: 'portrait',
        // Keep header image + body styles; nothing to strip out of this preview.
        stripSelectors: [],
      });

      // Step 3: save it. Filename: DATE_MODEL_STOCKNUMBER.pdf
      const filename = buffingAuthFilename(fields.date, fields.carModel, fields.stockNumber);
      await writeBlobToFolder(buffingFolder, filename, blob);

      // Step 4: if this is a pending request, flip status to approved.
      // Skip for resend mode (it's already approved) so we don't re-fire email.
      if (previewMode === 'approve' && preview.status === 'pending') {
        try {
          await api.approveBuffing(preview.id, fields);
        } catch (err) {
          // PDF is already saved — surface but don't fail the whole flow.
          console.warn('approveBuffing after PDF save failed:', err);
        }
      }

      // Cache the folder name so the badge stays correct.
      setFolderName(parent.name);
      setPreview(null);
      load();
      const savedPath = (year && week)
        ? `${parent.name} / ${year} YEAR / WEEK ${week} / BUFFING AUTORIZATION / ${filename}`
        : `${parent.name} / BUFFING AUTORIZATION / ${filename}`;
      alert(`✓ Saved\n${savedPath}`);
    } catch (err) {
      console.error(err);
      alert('Could not save PDF: ' + (err.message || err));
    } finally {
      setSavingPdf(false);
    }
  }

  /* Called from modal — actually send (with optional overrides from editing) */
  async function handleConfirmSend(overrides = {}) {
    if (!preview) return;
    setSending(true);
    try {
      if (previewMode === 'resend') {
        await api.resendBuffingEmail(preview.id, overrides);
        setPreview(null);
        load();
        alert('✓ Email resent successfully!');
      } else {
        const result = await api.approveBuffing(preview.id, overrides);
        setPreview(null);
        load();
        if (result && result.emailFailed) {
          alert('✓ Request approved!\n\n⚠️ Email could not be sent: ' + result.message);
        }
      }
    } catch (err) {
      alert('Error: ' + (err.message || 'Failed to send email'));
    } finally {
      setSending(false);
    }
  }

  async function handleReject(id) {
    if (!window.confirm('Reject this request?')) return;
    setActing(id);
    try {
      await api.rejectBuffing(id);
      load();
    } catch (err) {
      alert('Error: ' + (err.message || 'Failed to reject'));
    } finally {
      setActing(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this request permanently?')) return;
    setActing(id);
    try {
      await api.deleteBuffing(id);
      load();
    } catch (err) {
      alert('Error: ' + (err.message || 'Failed to delete'));
    } finally {
      setActing(null);
    }
  }

  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <div style={{ padding: '16px 16px 80px', maxWidth: 900, margin: '0 auto' }}>

      {/* Email preview modal */}
      {preview && (
        <EmailPreviewModal
          request={preview}
          mode={previewMode}
          sending={sending}
          savingPdf={savingPdf}
          onConfirm={handleConfirmSend}
          onSavePdf={handleSavePdf}
          onCancel={() => { if (!sending && !savingPdf) setPreview(null); }}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: BLACK, letterSpacing: 1 }}>BUFFING REQUESTS</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            Save the authorization as PDF, or send by email if available.
          </p>
        </div>
        {pending > 0 && (
          <span style={{ marginLeft: 'auto', background: '#dc2626', color: '#fff', borderRadius: 0, padding: '4px 12px', fontWeight: 700, fontSize: 13 }}>
            {pending} pending
          </span>
        )}
      </div>

      {/* Storage hint — tells admin where PDFs land. Shown only on browsers
          that actually support the File System Access API (Chrome / Edge). */}
      {supportsFileSystemAccess() && (
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 18, padding: '6px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', display: 'inline-block' }}>
          {folderName
            ? <>📁 PDFs save to <strong style={{ color: BLACK }}>{folderName}</strong> / [YEAR] / WEEK [N] / BUFFING AUTORIZATION /</>
            : <>📁 First "Save as PDF" will ask you to pick the OneDrive reports folder.</>}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[['pending','Pending'],['approved','Approved'],['rejected','Rejected'],['all','All']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{
            padding: '7px 16px', borderRadius: 0, border: filter === v ? `2px solid ${NAVY}` : '1px solid #e0e0e0',
            background: filter === v ? NAVY : '#fff', color: filter === v ? '#fff' : '#374151',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>{l}</button>
        ))}
        <button onClick={load} style={{ marginLeft: 'auto', padding: '7px 14px', borderRadius: 0, border: '1px solid #e0e0e0', background: '#f9fafb', color: '#374151', fontSize: 12, cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading...</p>}

      {!loading && requests.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>
          No {filter !== 'all' ? filter : ''} buffing requests.
        </div>
      )}

      {/* Request cards */}
      {requests.map(r => (
        <div key={r.id} style={{
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 0,
          padding: '18px 20px', marginBottom: 14,
          borderLeft: r.status === 'pending' ? `4px solid ${YELLOW}` : r.status === 'approved' ? '4px solid #16a34a' : '4px solid #dc2626',
        }}>
          {/* Top row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: BLACK }}>
                {r.stock_number} — {r.car_model}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                Submitted by <strong>{r.contractor_name}</strong> · {fmtDate(r.submitted_at)}
              </div>
            </div>
            {statusBadge(r.status)}
          </div>

          {/* Details grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
            {[
              ['Date', r.date],
              ['Department', r.department],
              ['Authorized By', r.authorized_by],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#f9fafb', borderRadius: 0, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: BLACK }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Scope */}
          <div style={{ background: '#f4f4f4', borderLeft: `3px solid ${BLACK}`, borderRadius: 0, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: BLACK }}>
            <strong>Scope:</strong> {r.scope}
          </div>

          {/* Actions */}
          {r.status === 'pending' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => handleApproveClick(r)}
                disabled={acting === r.id}
                style={{
                  flex: 1, padding: '12px', borderRadius: 0, border: 'none',
                  background: BLACK, color: YELLOW,
                  fontWeight: 800, fontSize: 14, cursor: 'pointer',
                }}>
                👁 Preview & Send Email
              </button>
              <button
                onClick={() => handleReject(r.id)}
                disabled={acting === r.id}
                style={{
                  padding: '12px 20px', borderRadius: 0, border: '1px solid #fca5a5',
                  background: '#fff1f1', color: '#dc2626',
                  fontWeight: 700, fontSize: 14, cursor: acting === r.id ? 'not-allowed' : 'pointer',
                }}>
                ✕ Reject
              </button>
            </div>
          )}

          {r.status !== 'pending' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 10, flexWrap: 'wrap' }}>
              {r.reviewed_by && (
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  {r.status === 'approved' ? 'Approved' : 'Rejected'} by {r.reviewed_by} · {fmtDate(r.reviewed_at)}
                </div>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {r.status === 'approved' && (
                  <button
                    onClick={() => handleResendClick(r)}
                    disabled={acting === r.id}
                    style={{
                      padding: '5px 12px', borderRadius: 0,
                      border: `1px solid ${NAVY}`, background: '#e8f2f7',
                      color: NAVY, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>
                    ✉ Resend Email
                  </button>
                )}
                <button
                  onClick={() => handleDelete(r.id)}
                  disabled={acting === r.id}
                  style={{
                    padding: '5px 12px', borderRadius: 0,
                    border: '1px solid #fca5a5', background: '#fff1f1',
                    color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                  🗑 Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
